import React, { useState, useContext } from "react";
import {
  Button,
  message,
  Modal,
  Form,
  Input,
  Spin,
  Select,
  DatePicker,
} from "antd";
import { FileTextOutlined, PrinterOutlined } from "@ant-design/icons";
import PDFService from "../utils/pdfService";
import moment from "moment";
import { AppContext } from "../context/AppContext";

const { Option } = Select;

const LoanStatementGenerator = ({
  loan,
  borrower,
  transactions = [],
  buttonProps = {},
  showModal = false,
}) => {
  const { user } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const handleGenerateStatement = async (customData = null) => {
    setLoading(true);
    try {
      // Use custom data from modal or default loan/borrower data
      let loanData, borrowerData, transactionData;

      if (customData) {
        ({
          loanData,
          borrowerData,
          transactions: transactionData,
        } = customData);
      } else {
        ({
          loanData,
          borrowerData,
          transactions: transactionData,
        } = PDFService.formatLoanDataForStatement(
          loan,
          borrower,
          transactions,
          user
        ));
      }

      const fullName = [borrower?.first_name, borrower?.last_name]
        .filter(Boolean)
        .join("_");
      const safeName = (fullName || borrower?.borrower_name || "borrower")
        .toString()
        .replace(/\W+/g, "_")
        .replace(/^_+|_+$/g, "");
      const filename = `loan_statement_${safeName}_${Date.now()}.pdf`;

      await PDFService.generateLoanStatement(
        loanData,
        borrowerData,
        transactionData,
        filename
      );

      message.success(
        "Loan statement generated and opened for printing successfully!"
      );
    } catch (error) {
      console.error("Error generating statement:", error);
      message.error("Failed to generate statement. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const customData = {
          loanData: {
            principal_amount: parseFloat(values.principal_amount),
            disbursement_date:
              values.disbursement_date?.toISOString() ||
              new Date().toISOString(),
            paid_amount: parseFloat(values.paid_amount || 0),
            loan_id: values.loan_reference,
            collateral_description: values.collateral_description,
            status_name: values.account_status,
            collateral_value: parseFloat(values.collateral_value || 0),
            issued_by: values.issued_by,
          },
          borrowerData: {
            names: values.borrower_name,
            nrc: values.borrower_nrc,
            phone: values.borrower_phone,
          },
          transactions: values.custom_transactions || transactions,
        };

        handleGenerateStatement(customData);
        setModalVisible(false);
        form.resetFields();
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const openModal = () => {
    // Debug: Log the raw borrower prop
    console.log('LoanStatementGenerator - Raw borrower prop:', borrower);
    console.log('LoanStatementGenerator - Raw loan prop:', loan);

    // Pre-populate form with existing data
    const { loanData, borrowerData } = PDFService.formatLoanDataForStatement(
      loan,
      borrower,
      transactions,
      user
    );

    console.log('LoanStatementGenerator - Formatted borrowerData:', borrowerData);

    // Get current user's name for issued_by field
    const userName = user?.name || (user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`.trim()
      : loanData.issued_by);

    form.setFieldsValue({
      borrower_name: borrowerData.names,
      borrower_nrc: borrowerData.nrc || '',
      borrower_phone: borrowerData.phone || '',
      disbursement_date: loanData.disbursement_date
        ? moment(loanData.disbursement_date)
        : moment(),
      principal_amount: loanData.principal_amount,
      paid_amount: loanData.paid_amount,
      loan_reference: loanData.loan_id,
      collateral_description: loanData.collateral_description,
      account_status: loanData.status_name,
      collateral_value: loanData.collateral_value,
      issued_by: userName,
    });

    setModalVisible(true);
  };

  if (!loan || !borrower) {
    return null;
  }

  return (
    <>
      {showModal ? (
        <Button
          icon={<FileTextOutlined />}
          onClick={openModal}
          loading={loading}
          {...buttonProps}
        >
          Loan Statement
        </Button>
      ) : (
        <Button
          icon={<PrinterOutlined />}
          onClick={() => handleGenerateStatement()}
          loading={loading}
          {...buttonProps}
        >
          Print Statement
        </Button>
      )}

      <Modal
        title="Generate Loan Statement"
        open={modalVisible}
        onOk={handleModalSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
        confirmLoading={loading}
        okText="Generate Statement"
      >
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            style={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            <h4>Borrower Information</h4>
            <Form.Item
              label="Borrower Name"
              name="borrower_name"
              rules={[{ required: true }]}
            >
              <Input placeholder="PHYLLIS YAMUKWENDA KATEBE" />
            </Form.Item>

            <Form.Item
              label="NRC Number"
              name="borrower_nrc"
              rules={[{ required: true }]}
            >
              <Input placeholder="199851/71/1" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="borrower_phone"
              rules={[{ required: true }]}
            >
              <Input placeholder="+260977660716" />
            </Form.Item>

            <h4>Loan Details</h4>
            <Form.Item
              label="Disbursement Date"
              name="disbursement_date"
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              label="Principal Amount (ZMW)"
              name="principal_amount"
              rules={[{ required: true }]}
            >
              <Input type="number" placeholder="30000.00" />
            </Form.Item>

            <Form.Item label="Amount Paid (ZMW)" name="paid_amount">
              <Input type="number" placeholder="1000.00" />
            </Form.Item>

            <Form.Item
              label="Loan Reference Number"
              name="loan_reference"
              rules={[{ required: true }]}
            >
              <Input placeholder="123456" />
            </Form.Item>

            <Form.Item
              label="Collateral Description"
              name="collateral_description"
              rules={[{ required: true }]}
            >
              <Input placeholder="SILVER TOYOTA RACTIS BBA9647ZM" />
            </Form.Item>

            <Form.Item
              label="Account Status"
              name="account_status"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select status">
                <Option value="ACTIVE">ACTIVE</Option>
                <Option value="PENDING">PENDING</Option>
                <Option value="SETTLED">SETTLED</Option>
                <Option value="DEFAULT">DEFAULT</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Collateral Value at Disbursement (ZMW)"
              name="collateral_value"
            >
              <Input type="number" placeholder="65000.00" />
            </Form.Item>

            <Form.Item
              label="Statement Issued By"
              name="issued_by"
              rules={[{ required: true }]}
            >
              <Input placeholder="MASHEDA BELESHI" />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default LoanStatementGenerator;
